import { extractVideoId, parseErrorType, checkIsYouTubeShorts } from '../../../utils';
import { ERROR_TYPES } from '../../../components/Modal/ErrorModal';
import { useTranslation } from 'react-i18next';
import { useDuplicateChecker } from './useDuplicateChecker';
import { useYouTubeAPI } from './useYouTubeAPI';
import { useHistoryCopier } from './useHistoryCopier';
import { useFirestoreCreator } from './useFirestoreCreator';
import { AI_API_URL } from '../../../constants';

export const useAnalysisStarter = (
  userId,
  inputUrl,
  setInputUrl,
  setInputTitle,
  loading,
  analyzeLoading,
  setLoading,
  setAnalyzeLoading,
  history,
  showErrorModal,
  parseISO8601Duration,
  setVideoDuration
) => {
  const { i18n } = useTranslation();
  
  // 분리된 훅들 사용
  const { checkLocalDuplicate, checkPersonalHistory, checkGlobalHistory } = useDuplicateChecker();
  const { fetchVideoData } = useYouTubeAPI();
  const { copyFromGlobalHistory } = useHistoryCopier();
  const { createNewHistory } = useFirestoreCreator();

  const handleAnalyze = async () => {
    // ✅ 중복 분석 방지: 이미 분석 중이면 건너뛰기
    if (loading || analyzeLoading) {
      console.log('🚫 [중복 방지] 이미 분석 중이므로 요청 무시');
      return;
    }

    if (!userId) {
      console.error("사용자 ID가 없습니다.");
      alert("로그인이 필요합니다.");
      return;
    }

    if (!inputUrl || !inputUrl.trim()) {
      showErrorModal(ERROR_TYPES.INVALID_URL);
      return;
    }

    // ✅ 분석 시작 시 loading 상태 설정
    setLoading(true);
    setAnalyzeLoading(true);

    try {
      // 쇼츠 체크를 먼저 수행 (API 호출 전)
      if (checkIsYouTubeShorts(inputUrl)) {
        showErrorModal(ERROR_TYPES.YOUTUBE_SHORTS);
        return;
      }

      const extractedVideoId = extractVideoId(inputUrl);
      if (!extractedVideoId) {
        showErrorModal(ERROR_TYPES.INVALID_URL);
        return;
      }

      // 🔥 자막 사전 체크 기능 재활성화 - URL 검증 문제 해결됨
      console.log(`🔍 [자막 체크] 활성화됨 - 사전 체크로 자막 없음/실시간 방송 미리 감지`);

      // ✅ 1단계: 개인 히스토리에서 같은 videoId + language가 있는지 확인 (로컬)
      if (checkLocalDuplicate(extractedVideoId, i18n.language, history, showErrorModal, setInputUrl, setInputTitle)) {
        return;
      }

      // ✅ 2단계: 개인 히스토리에서 중복 체크 (API)
      const personalDuplicateFound = await checkPersonalHistory(
        extractedVideoId, 
        i18n.language, 
        userId, 
        showErrorModal, 
        setInputUrl, 
        setInputTitle
      );
      
      if (personalDuplicateFound) {
        return;
      }

      // ✅ 3단계: 광역 히스토리에서 중복 체크 및 복사
      const globalHistoryExists = await checkGlobalHistory(extractedVideoId, i18n.language, userId);
      
      if (globalHistoryExists) {
        const copyResult = await copyFromGlobalHistory(
          extractedVideoId, 
          i18n.language, 
          userId, 
          setInputUrl, 
          setInputTitle
        );
        
        if (copyResult) {
          return copyResult;
        }
        // 복사 실패 시 새로 분석 진행
      }

      // ✅ 4단계: 새로 분석 시작 - YouTube API를 호출하여 영상 정보 가져오기
      console.log(`🚀 [새 분석] ${extractedVideoId} (${i18n.language}) 분석 시작`);
      
      const videoResult = await fetchVideoData(extractedVideoId, inputUrl, parseISO8601Duration, setVideoDuration);
      
      if (!videoResult) {
        showErrorModal(ERROR_TYPES.VIDEO_NOT_FOUND);
        return;
      }

      const { videoTitle } = videoResult;

      // ✅ 5단계: 사전 체크 + Firestore 히스토리 생성 (자막 없음/실시간 방송은 여기서 감지)
      const createResult = await createNewHistory(
        inputUrl, 
        extractedVideoId, 
        videoTitle, 
        userId, 
        i18n.language, 
        setInputUrl, 
        setInputTitle
      );
      
      return createResult;
      
    } catch (error) {
      console.error('🚫 [분석 에러]:', error);
      
      // 🔍 사전 체크 에러 타입 파싱 개선
      let errorType = ERROR_TYPES.UNKNOWN_ERROR;
      let errorMessage = error.message || '';
      
      // 사전 체크에서 발생한 특별한 에러들 처리
      if (errorMessage.startsWith('NO_CAPTIONS:')) {
        errorType = ERROR_TYPES.NO_CAPTIONS;
        console.log('❌ [에러 처리] 자막 없음 에러 감지');
      } else if (errorMessage.startsWith('LIVE_STREAM:')) {
        errorType = ERROR_TYPES.LIVE_STREAM;
        console.log('❌ [에러 처리] 실시간 방송 에러 감지');
      } else if (errorMessage.includes(':')) {
        // 기존 에러 타입 파싱 로직
        const [type] = errorMessage.split(':');
        if (Object.values(ERROR_TYPES).includes(type)) {
          errorType = type;
        } else {
          errorType = parseErrorType(errorMessage);
        }
      } else {
        // parseErrorType 함수 사용
        errorType = parseErrorType(errorMessage);
      }
      
      // 네트워크 에러 체크
      if (!navigator.onLine) {
        showErrorModal(ERROR_TYPES.NETWORK_ERROR, '', () => handleAnalyze());
      } else if (errorMessage.includes('YouTube API')) {
        showErrorModal(ERROR_TYPES.VIDEO_NOT_FOUND, '', () => handleAnalyze());
      } else {
        // 🔍 사전 체크 에러는 재시도 버튼 없이 표시 (자막 없음, 실시간 방송)
        if (errorType === ERROR_TYPES.NO_CAPTIONS || errorType === ERROR_TYPES.LIVE_STREAM) {
          showErrorModal(errorType, '', null); // 재시도 버튼 없음
        } else {
          showErrorModal(errorType, '', () => handleAnalyze()); // 재시도 버튼 있음
        }
      }
      return;
    } finally {
      // ✅ 분석 완료 시 loading 상태 해제
      setLoading(false);
      // ✅ 에러 발생 시에도 analyzeLoading 상태 해제 (에러 모달이 표시되도록)
      setAnalyzeLoading(false);
    }
  };

  return {
    handleAnalyze,
  };
}; 